import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'

import { api } from '../lib/api'
import { useSettings } from '../components/RequireAuth'

interface Account {
  id: number
  email: string
  name: string
  role: string
}

interface AuthConfig {
  dev_login: boolean
  accounts: Account[]
}

interface LoginResponse {
  sent: boolean
  message: string
  dev_link: string | null
}

export function Login() {
  const [params] = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [useLink, setUseLink] = useState(false)
  const expired = params.get('error') === 'expired'
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const config = useQuery({
    queryKey: ['auth-config'],
    queryFn: () => api.get<AuthConfig>('/auth/config'),
    retry: false,
  })

  const request = useMutation({
    mutationFn: () => api.post<LoginResponse>('/auth/request', { email }),
  })

  // The everyday path. Passwords are issued by the admin, never chosen and never
  // changed here, so there is deliberately no "forgot password" link: the recovery
  // route is asking Fernando, which is also the only way to prove who is asking.
  const signIn = useMutation({
    mutationFn: () => api.post('/auth/login', { email, password }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] })
      navigate('/', { replace: true })
    },
  })

  // Local development only. The endpoint that backs this is not registered on the server
  // unless it is running on localhost, without HTTPS, and without a mail provider.
  const devLogin = useMutation({
    mutationFn: (address: string) => api.post('/auth/dev-login', { email: address }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] })
      navigate('/', { replace: true })
    },
  })

  // Nothing guards the app while open access is on, so a sign-in form here is a dead end:
  // a bookmark or a restored tab would leave you staring at a login you cannot complete.
  const settings = useSettings()
  if (settings.isLoading) {
    return <div className="bootsplash">Loading…</div>
  }
  if (settings.data?.open_access) {
    return <Navigate to="/" replace />
  }

  return (
    <div className="loginpage">
      <div className="loginbox">
        <div className="loginbrand">
          Old Street<br />Studios <span>Ops</span>
        </div>

        {expired && !request.data && (
          <div className="alarm">That link had already been used, or it expired. Here&rsquo;s another.</div>
        )}

        {config.data?.dev_login && (
          <div className="devlogin">
            <div className="devlogin-head">
              <span className="badge b-needs_triage">Local dev</span>
              <span>Skip the email — sign in as:</span>
            </div>
            <div className="devlogin-btns">
              {config.data.accounts.map((account) => (
                <button
                  key={account.id}
                  type="button"
                  className="devbtn"
                  disabled={devLogin.isPending}
                  onClick={() => devLogin.mutate(account.email)}
                >
                  <b>{account.name}</b>
                  <i>{account.role}</i>
                </button>
              ))}
            </div>
            <p className="devnote">
              Turns itself off the moment <code>COOKIE_SECURE</code> is true,
              <code>BREVO_API_KEY</code> is set, or the app leaves localhost.
            </p>
          </div>
        )}

        {request.data ? (
          <div className="loginsent">
            <p><b>Check your email.</b></p>
            <p className="muted">{request.data.message}</p>
            {request.data.dev_link && (
              <p className="devlink">
                No mail provider configured, so here is the link:{' '}
                <a href={request.data.dev_link}>sign in</a>
              </p>
            )}
          </div>
        ) : useLink ? (
          <form
            className="loginform"
            onSubmit={(event) => {
              event.preventDefault()
              request.mutate()
            }}
          >
            <p className="muted">
              We&rsquo;ll email you a link that signs you in. It works once and expires in
              15&nbsp;minutes.
            </p>
            <label>
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@oldstreetstudios.com"
                autoFocus
                required
              />
            </label>
            <button className="btn" type="submit" disabled={!email || request.isPending}>
              {request.isPending ? 'Sending…' : 'Email me a link'}
            </button>
            <button type="button" className="linkbtn" onClick={() => setUseLink(false)}>
              Use a password instead
            </button>
            {request.error && <span className="v-alert">Something went wrong. Try again.</span>}
          </form>
        ) : (
          <form
            className="loginform"
            onSubmit={(event) => {
              event.preventDefault()
              signIn.mutate()
            }}
          >
            <label>
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@oldstreetstudios.com"
                autoComplete="username"
                autoFocus
                required
              />
            </label>
            <label>
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            <button
              className="btn"
              type="submit"
              disabled={!email || !password || signIn.isPending}
            >
              {signIn.isPending ? 'Signing in…' : 'Sign in'}
            </button>
            {signIn.error && (
              <span className="v-alert">That email and password do not match.</span>
            )}
            <p className="hint">
              Passwords are issued, not chosen — there is nothing to reset here. Ask whoever
              set yours up for a new one, or{' '}
              <button type="button" className="linkbtn inline" onClick={() => setUseLink(true)}>
                email yourself a link
              </button>
              .
            </p>
          </form>
        )}
      </div>
    </div>
  )
}
